import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Camera, CheckCircle2 } from "lucide-react";
import { to12h } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLOT_LABELS = {
  front: "Front of vehicle", rear: "Rear of vehicle", driver_side: "Driver side",
  passenger_side: "Passenger side", interior_front: "Interior / front seats",
  interior_rear: "Rear seats / interior", fuel_gauge: "Fuel gauge", odometer: "Odometer / mileage",
};

export default function PreReturn() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [photos, setPhotos] = useState({});
  const [extra, setExtra] = useState([]);
  const [mileage, setMileage] = useState("");
  const [fuel, setFuel] = useState("");
  const [comments, setComments] = useState("");
  const [noDamage, setNoDamage] = useState(false);
  const [uploading, setUploading] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/pre-return/${token}`).then((r) => { setInfo(r.data); if (r.data.submitted) setDone(true); })
      .catch(() => setErr("This link is invalid or has expired. Please contact AutoHire STL."));
  }, [token]);

  const upload = async (slot, file) => {
    if (!file) return;
    setUploading(slot);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/pre-return/${token}/upload`, fd);
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      if (slot === "extra") setExtra((p) => [...p, url]);
      else setPhotos((p) => ({ ...p, [slot]: url }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed — try again."); }
    finally { setUploading(""); }
  };

  const submit = async () => {
    const missing = (info?.slots || []).filter((s) => !photos[s]);
    if (missing.length) { toast.error(`Please add: ${missing.map((s) => SLOT_LABELS[s]).join(", ")}`); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/pre-return/${token}/submit`,
        { photos, extra_photos: extra, mileage, fuel_level: fuel, comments, no_new_damage: noDamage });
      toast.success(data.message);
      setDone(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit — try again."); }
    finally { setSaving(false); }
  };

  if (err) return <div className="min-h-screen bg-black text-white flex items-center justify-center p-6 text-center">{err}</div>;
  if (!info) return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="animate-spin text-[#F2B705]" size={28} /></div>;
  if (done) return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center p-6 text-center gap-3">
      <CheckCircle2 className="text-emerald-400" size={44} />
      <h1 className="font-display font-bold text-2xl">Pre-Return Inspection Received</h1>
      <p className="text-white/60 text-sm max-w-md">Thank you{info.name ? `, ${info.name}` : ""}! Please return the {info.vehicle} at your scheduled time — {info.return_date} {info.return_time}. AutoHire will complete the official check-in inspection at return.</p>
    </div>
  );

  const Slot = ({ slot }) => (
    <label data-testid={`pre-slot-${slot}`} className={`block rounded-xl border-2 border-dashed p-3 text-center cursor-pointer transition-colors ${photos[slot] ? "border-emerald-500/60 bg-emerald-500/5" : "border-white/20 hover:border-[#F2B705]/60"}`}>
      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload(slot, e.target.files?.[0])} />
      {photos[slot] ? <img src={photos[slot]} alt={slot} className="h-24 w-full object-cover rounded-md mb-1" />
        : <Camera className="mx-auto text-white/40 mb-1" size={22} />}
      <span className={`text-xs font-semibold ${photos[slot] ? "text-emerald-300" : "text-white/70"}`}>
        {uploading === slot ? "Uploading…" : SLOT_LABELS[slot]}{photos[slot] ? " ✓" : " *"}
      </span>
    </label>
  );

  return (
    <div className="min-h-screen bg-black text-white p-4 pb-16">
      <div className="max-w-lg mx-auto space-y-4">
        <div>
          <h1 className="font-display font-bold text-2xl text-[#F2B705]">Pre-Return Inspection</h1>
          <p className="text-white/60 text-sm">{info.vehicle} — return scheduled {info.return_date} {to12h(info.return_time)}. Take all 8 required photos, then submit. This does not replace AutoHire's official check-in inspection.</p>
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          {info.slots.map((s) => <Slot key={s} slot={s} />)}
        </div>
        <label className="block rounded-xl border-2 border-dashed border-white/15 p-3 text-center cursor-pointer">
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload("extra", e.target.files?.[0])} />
          <span className="text-xs text-white/60">{uploading === "extra" ? "Uploading…" : `+ Add damage / extra photo (optional)${extra.length ? ` — ${extra.length} added` : ""}`}</span>
        </label>
        <div className="grid grid-cols-2 gap-2.5">
          <label className="text-xs text-white/60">Current mileage
            <input data-testid="pre-mileage" className="mt-1 w-full bg-[#0C0C0C] border border-white/15 rounded px-3 py-2 text-white" value={mileage} onChange={(e) => setMileage(e.target.value)} inputMode="numeric" /></label>
          <label className="text-xs text-white/60">Fuel level (e.g. 3/4)
            <input data-testid="pre-fuel" className="mt-1 w-full bg-[#0C0C0C] border border-white/15 rounded px-3 py-2 text-white" value={fuel} onChange={(e) => setFuel(e.target.value)} /></label>
        </div>
        <label className="text-xs text-white/60 block">Notes / comments
          <textarea data-testid="pre-comments" rows={2} className="mt-1 w-full bg-[#0C0C0C] border border-white/15 rounded px-3 py-2 text-white" value={comments} onChange={(e) => setComments(e.target.value)} /></label>
        <label className="flex items-start gap-2.5 text-sm text-white/80">
          <input data-testid="pre-no-damage" type="checkbox" className="h-4 w-4 mt-0.5 accent-[#F2B705]" checked={noDamage} onChange={(e) => setNoDamage(e.target.checked)} />
          I confirm there is no new damage to report on this vehicle.
        </label>
        <button data-testid="pre-submit" onClick={submit} disabled={saving}
          className="w-full bg-[#F2B705] text-black font-bold py-3 rounded-xl hover:bg-white transition-colors disabled:opacity-50">
          {saving ? "Submitting…" : "Submit Pre-Return Inspection"}
        </button>
      </div>
    </div>
  );
}
